import { useDeployment } from '../../api/builds.js';
import { Alert, Card, Spinner } from '../../components/ui.js';
import { AndroidAppCard } from '../../components/AndroidAppCard.js';
import { DeployStatusBadge } from '../../status.js';
import { useProjectContext } from './ProjectShell.js';

/**
 * Founder-facing live testing: where the founder opens and tries their app as it
 * comes together. Mirrors the staff Live testing view, but read-only — founders
 * cannot trigger a deploy (that is a developer action), so there is no button and
 * no internal failure detail.
 */
export function LiveTestingPage() {
  const { project } = useProjectContext();
  const deployment = useDeployment(project.id);

  if (deployment.isPending)
    return (
      <Card>
        <Spinner />
      </Card>
    );
  if (deployment.isError || !deployment.data)
    return (
      <Card>
        <Alert>Could not load your app’s status just now. Please try again shortly.</Alert>
      </Card>
    );

  const d = deployment.data;
  const inFlight = d.status === 'provisioning' || d.status === 'building';

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <h2 className="font-title text-2xl tracking-wide">Live testing</h2>
          <DeployStatusBadge status={d.status} />
        </div>

        {d.liveUrl ? (
          <div className="mb-4 rounded-md bg-neutral/40 p-3">
            <div className="text-xs uppercase tracking-wide opacity-60">Your app is live at</div>
            <a
              href={d.liveUrl}
              target="_blank"
              rel="noreferrer"
              className="break-all font-medium text-primary underline"
            >
              {d.liveUrl.replace(/^https:\/\//, '')}
            </a>
            {d.lastDeployedAt ? (
              <p className="mt-1 text-xs opacity-60">
                Last updated {new Date(d.lastDeployedAt).toLocaleString()}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="mb-4 text-sm opacity-70">
            {inFlight
              ? 'Your app is being set up — the link will appear here as soon as it’s up.'
              : 'Your app will appear here once the first story has been built and deployed.'}
          </p>
        )}

        {d.adminLiveUrl ? (
          <div className="mb-4 rounded-md bg-neutral/40 p-3">
            <div className="text-xs uppercase tracking-wide opacity-60">Admin portal</div>
            <a
              href={d.adminLiveUrl}
              target="_blank"
              rel="noreferrer"
              className="break-all font-medium text-primary underline"
            >
              {d.adminLiveUrl.replace(/^https:\/\//, '')}
            </a>
          </div>
        ) : null}

        {d.status === 'failed' ? (
          <Alert kind="info">
            We hit a snag deploying the latest version — our team is on it.
            {d.liveUrl ? ' The last working version stays available at the link above.' : ''}
          </Alert>
        ) : null}

        <p className="mt-4 text-xs opacity-60">
          Each story is deployed here automatically as it’s built, so you can try your app as it
          comes together.
        </p>
      </Card>

      {d.liveUrl ? (
        <Card>
          <h2 className="mb-3 font-title text-2xl tracking-wide">On your phone</h2>
          <AndroidAppCard projectId={project.id} canBuild={false} />
        </Card>
      ) : null}
    </div>
  );
}
